import {
  FormatReference,
  FormatStatus,
  IFormatRepository,
  FormatSet,
  FormatGroup,
} from "../../../domain/format";
import { ReferenceFileInformation } from "../../../domain/document";
import {
  Directory, RootDirectory,
} from "../../../domain/drawing";
import { AuthenticatedService } from "../..";
import { ObjectTranslator } from ".";
import {
  Table,
  LightTable,
} from "../../../domain/system/table";
import { IFormatGroupBean, INewDocumentCreatbleCheckResultBean } from "./beans";
import { ManagementTokenPublishResult } from "../../../domain/system";
import { ManagementTokenTranslator } from "../../system";

export class FormatRepository extends AuthenticatedService implements IFormatRepository {
  constructor(getToken: () => string) {
    super(getToken);
  }

  private translator = new ObjectTranslator();

  private isEmpty(value: string) {
    return (value === undefined || value === null || value === "");
  }

  async getFormatReference(id: number): Promise<FormatReference> {
    if (!id) {
      throw new Error("idが有効な数値ではありません.");
    }
    const url = "/api/format/reference/" + id;
    const result = await this.get(url);
    return this.translator.toFormat(() => this.token, result.data);
  }

  async getFormatReferences(ids: number[]): Promise<FormatReference[]> {
    const url = "/api/format/references";
    const result = await this.post(url, ids);
    return Array.isArray(result.data)
      ? result.data.map(v => this.translator.toFormat(() => this.token, v))
      : [];
  }

  async getFormats(shouldIgnoreChained: boolean, shouldIncludeReferenceableGroupFormats: boolean): Promise<Directory<string, FormatReference>[]> {
    const url = "/api/format/folders";
    const query = {
      params: {
        shouldIgnoreChained: shouldIgnoreChained,
        shouldIncludeReferenceableDepartmentGroupFormats: shouldIncludeReferenceableGroupFormats,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toFormatsTree(() => this.token, result.data);
  }

  async getCommonDepartmentFavoriteFormats(shouldIgnoreChained: boolean, shouldIncludeReferenceableGroupFormats: boolean): Promise<Directory<string, FormatReference>[]> {
    const url = "/api/format/common-departmentfavorites";
    const query = {
      params: {
        shouldIgnoreChained: shouldIgnoreChained,
        shouldIncludeReferenceableDepartmentGroupFormats: shouldIncludeReferenceableGroupFormats,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toFormatsTree(() => this.token, result.data);
  }

  async getDepartmentFavoriteFormats(id: string, shouldIgnoreChained: boolean, shouldIncludeReferenceableGroupFormats: boolean): Promise<Directory<string, FormatReference>[]> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const url = "/api/format/departmentfavorites";
    const query = {
      params: {
        departmentcode: id,
        shouldIgnoreChained: shouldIgnoreChained,
        shouldIncludeReferenceableDepartmentGroupFormats: shouldIncludeReferenceableGroupFormats,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toFormatsTree(() => this.token, result.data);
  }

  async getUserFavoriteFormats(id: string, shouldIgnoreChained: boolean, shouldIncludeReferenceableGroupFormats: boolean): Promise<Directory<string, FormatReference>[]> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const url = "/api/format/userfavorites";
    const query = {
      params: {
        usercode: id,
        shouldIgnoreChained: shouldIgnoreChained,
        shouldIncludeReferenceableDepartmentGroupFormats: shouldIncludeReferenceableGroupFormats,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toFormatsTree(() => this.token, result.data);
  }

  async getFormatSets(shouldIgnoreChained: boolean): Promise<RootDirectory<string, FormatSet>> {
    const url = "/api/format/groups";
    const query = {
      params: {
        shouldIgnoreChained: shouldIgnoreChained,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toFormatSetTree(() => this.token, result.data);
  }

  async getDepartmentFavoriteFormatSets(id: string, shouldIgnoreChained: boolean): Promise<RootDirectory<string, FormatSet>> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const url = "/api/format/departmentgroups";
    const query = {
      params: {
        departmentcode: id,
        shouldIgnoreChained: shouldIgnoreChained,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toFormatSetTree(() => this.token, result.data);
  }

  async getUserFavoriteFormatSets(id: string, shouldIgnoreChained: boolean): Promise<RootDirectory<string, FormatSet>> {
    if (this.isEmpty(id)) { throw new Error("id is empty!!!"); }
    const url = "/api/format/usergroups";
    const query = {
      params: {
        usercode: id,
        shouldIgnoreChained: shouldIgnoreChained,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toFormatSetTree(() => this.token, result.data);
  }

  async getUserDefinition(key: string): Promise<Table> {
    if (this.isEmpty(key)) { throw new Error("key is empty!!!"); }
    // TODO: cache
    const url = "/api/format/userdefinition";
    const query = {
      params: {
        fileName: key,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toUserDefinition(result.data);
  }

  async getUserDefinitionAsLightTable(key: string): Promise<LightTable> {
    if (this.isEmpty(key)) { throw new Error("key is empty!!!"); }
    const url = "/api/format/userdefinition";
    const query = {
      params: {
        fileName: key,
      }
    };
    const result = await this.get(url, query);
    return this.translator.toUserDefinitionAsLightTable(result.data);
  }

  async isAvailableUserDefinition(key: string): Promise<boolean> {
    if (this.isEmpty(key)) { throw new Error("key is empty!!!"); }
    const url = "/api/format/is-available-userdefinition";
    const query = {
      params: {
        fileName: key,
      }
    };
    const result = await this.get(url, query);
    return !!result.data;
  }

  async setCommonDepartmentFavoriteFormats(formats: Directory<string, FormatReference>[]): Promise<void> {
    const url = "/api/format/common-departmentfavorites/save";
    const data = {
      token: this.token,
      folders: this.translator.toFormatFolderRootBean(formats),
    };
    await this.post(url, data);
  }

  async setDepartmentFavoriteFormats(id: string, formats: Directory<string, FormatReference>[]): Promise<void> {
    const url = "/api/format/departmentfavorites/save";
    const data = {
      token: this.token,
      departmentCode: id,
      folders: this.translator.toFormatFolderRootBean(formats),
    };
    await this.post(url, data);
  }

  async setUserFavoriteFormats(id: string, formats: Directory<string, FormatReference>[]): Promise<void> {
    const url = "/api/format/userfavorites/save";
    const data = {
      token: this.token,
      userCode: id,
      folders: this.translator.toFormatFolderRootBean(formats),
    };
    await this.post(url, data);
  }

  async saveCommonDepartmentFormatSets(root: RootDirectory<string, FormatSet>): Promise<void> {
    const url = "/api/format/groups/save";
    const data = {
      token: this.token,
      folders: this.translator.toFormatFolderRootBeanFromFormatSetDirectory(root),
    };
    await this.post(url, data);
  }

  async saveDepartmentFormatSets(id: string, root: RootDirectory<string, FormatSet>): Promise<void> {
    const url = "/api/format/departmentgroups/save";
    const data = {
      token: this.token,
      departmentCode: id,
      folders: this.translator.toFormatFolderRootBeanFromFormatSetDirectory(root),
    };
    await this.post(url, data);
  }

  async saveUserFormatSets(id: string, root: RootDirectory<string, FormatSet>): Promise<void> {
    const url = "/api/format/usergroups/save";
    const data = {
      token: this.token,
      userCode: id,
      folders: this.translator.toFormatFolderRootBeanFromFormatSetDirectory(root),
    };
    await this.post(url, data);
  }

  async getFormatStatus(originalId: number): Promise<FormatStatus> {
    if (originalId == null) { throw new Error("originalId is empty!!!"); }
    const url = "/api/format/" + originalId + "/status";
    const result = await this.get(url);
    return this.translator.toFormatStatus(result.data);
  }

  async getLatestFormatReferenceFilesInformation(formatOriginalId: number): Promise<ReferenceFileInformation[]> {
    const url = "/api/format/latest-format-reference-files/" + formatOriginalId;
    const result = await this.get(url);
    return this.translator.toReferenceFilesInformation(result.data);
  }

  async getWizards(key: string): Promise<Directory<string, FormatReference>[]> {
    const result = await this.get(
      "/api/format/wizard/folders",
      {
        params: {
          wizardKey: key,
        }
      });
    return this.translator.toFormatsTree(() => this.token, result.data);
  }

  async isNewDocumentCreatable(formatOriginalId: number): Promise<boolean> {
    const result = await this.get("/api/format/" + formatOriginalId + "/is-creatable");
    const data = result.data as INewDocumentCreatbleCheckResultBean;
    return data && data.isOk;
  }

  async getQuotableDestinationFormatIds(formatOriginalId: number): Promise<number[]> {
    const result = await this.get("/api/format/" + formatOriginalId + "/quotable-destination-formats");
    return result.data as number[];
  }

  async getFormatManagementToken(managementKey: string, isForcibly: boolean): Promise<ManagementTokenPublishResult> {
    return this.getManagementToken(managementKey, false, isForcibly);
  }

  async getFormatSetManagementToken(managementKey: string, isForcibly: boolean): Promise<ManagementTokenPublishResult> {
    return this.getManagementToken(managementKey, true, isForcibly);
  }

  private async getManagementToken(managementKey: string, isFormatSet: boolean, isForcibly: boolean): Promise<ManagementTokenPublishResult> {
    const query = {
      params: {
        key: !!managementKey ? managementKey : "",
        isForcibly: !!isForcibly,
        isFormatSet: !!isFormatSet,
      }
    };
    const result = await this.get("/api/format/publish-management-token", query);
    return ManagementTokenTranslator.toManagementTokenPublishResult(result.data);
  }

  async isSingleEntry(formatOriginalId: number): Promise<boolean> {
    const result = await this.get("/api/format/" + formatOriginalId + "/is-single-entry");
    return result.data as boolean;
  }

  async getFormatGroupInformation(formatGroupId: number): Promise<FormatGroup> {
    const result = await this.get("/api/format/groups/" + formatGroupId);
    return this.toFormatGroup(result.data);
  }

  private toFormatGroup(bean: IFormatGroupBean): FormatGroup {
    if (!bean) { return null; }

    const formatGroup = new FormatGroup();
    formatGroup.id = bean.id;
    formatGroup.name = bean.name;
    formatGroup.isDeleted = !!bean.isDeleted;
    formatGroup.formats = !!bean.formats ? bean.formats.map(f => this.translator.toFormat(() => this.token, f)) : [];
    return formatGroup;
  }
}
